import React, { useRef } from "react";
import Logo from "../../../resources/images/logo.svg";
import Favicon from "../../../resources/images/favicon/favicon-32x32.png";
import LanguageSelector, { changeLanguage, LanguageItem } from "../language/LanguageSelector";
import "./Header.scss";
import { useTranslation } from "react-i18next";
import { ContentProps } from "../../types/ContentProps";
import tenantService from "../../services/TenantService";
import NavigationLink, { LinkItem } from "../menu/NavigationLink";
import { CookieBar } from "../cookieBar/CookieBar";
import { ContentConfig, Language, NavigationPage } from "../../types/ContentConfig";
import { Tenant } from "../../types/Tenant";

const Header = (props: ContentProps) => {
    const { t } = useTranslation();
    const menu = useRef<HTMLDivElement>(null);
    const [config, setConfig] = React.useState<ContentConfig>();
    const [isPageLoaded, setIsPageLoaded] = React.useState<boolean>(false);
    const [tenant, setTenant] = React.useState<string>("");

    React.useEffect(() => {
        initData()
            .then(() => setIsPageLoaded(true))
            .catch(() => setIsPageLoaded(true));
    }, []);

    const initData = async () => {
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement("link");
            // @ts-ignore
            link.rel = "icon";
            document.getElementsByTagName("head")[0].appendChild(link);
        }
        // @ts-ignore
        link.href = Favicon;
        const tenant = await tenantService.getTenant();
        const config = await tenantService.getConfig(tenant);
        setConfig(config);
        setTenant(tenant);
    };

    const changeLanguageHeader = (language: string) => {
        changeLanguage(language);
        trackLanguageChange(language);
    };

    const trackLanguageChange = (language: string) => {
        // @ts-ignore expecting that etracker already included in the window object
        const etracker = window["_etracker"];

        if (etracker && etracker.isTrackingEnabled()) {
            try {
                // @ts-ignore
                etracker.sendEvent(new et_ClickEvent("LanguageSelect-" + language, ""));
            } catch (err) {}
        }
    };

    const isLanguageAvailable = (language: Language): boolean => {
        if (!config) {
            return false;
        }

        return config.languages.includes(language);
    };

    const isMenuLinkAvailable = (navigation: NavigationPage): boolean => {
        if (!config) {
            return false;
        }

        return config.navigation.includes(navigation);
    };

    const getCookieBarLink = () => {
        if (tenant === Tenant.ES_EOS) {
            return "/services/content/cookiePolicy.html";
        }
        return "/services/content/privacyPolicy.html";
    };

    const getHelpLink = () => {
        if (tenant === Tenant.DE_DID) {
            return "https://de.eos-solutions.com/customers?utm_campaign=serviceportal&utm_medium=link&utm_source=serviceportal";
        }
        if (tenant === Tenant.FR_EOS) {
            return "https://eos-france.com/debiteurs#faq";
        }
        return "/services/content/generalQuestions.html";
    };

    return (
        <div className="eos-header-wrapper">
            <CookieBar
                text={t("COOKIE_BANNER.TEXT")}
                privacyLink={getCookieBarLink()}
                privacyText={t("COOKIE_BANNER.LINK_TEXT")}
                buttonName={t("COOKIE_BANNER.BUTTON")}
                cookieName={"spot_cookieBarShow"}
                cookieValue={"true"}
            />

            <div className="eos-header">
                <div className={"header-logo"} role={"banner"} aria-label={t("HEADER.COMPANY")} aria-labelledby={"logo"}>
                    <a href="/" tabIndex={0}>
                        <img className={"eos-logo"} src={Logo} alt={"EOS Logo"} id={"logo"} />
                        <span className={"logo-label"}>{t("HEADER.COMPANY")}</span>
                    </a>
                </div>

                <nav className="eos-header-nav">
                    <LanguageSelector className={"header-language"} onClick={trackLanguageChange}>
                        <LanguageItem value={"de"} label={t("HEADER.LANGUAGE.GERMAN")} show={isLanguageAvailable(Language.DE)} />
                        <LanguageItem value={"en"} label={t("HEADER.LANGUAGE.ENGLISH")} show={isLanguageAvailable(Language.EN)} />
                        <LanguageItem value={"pl"} label={t("HEADER.LANGUAGE.POLISH")} show={isLanguageAvailable(Language.PL)} />
                        <LanguageItem value={"tr"} label={t("HEADER.LANGUAGE.TUERKISCH")} show={isLanguageAvailable(Language.TR)} />
                        <LanguageItem value={"es"} label={t("HEADER.LANGUAGE.SPANISH")} show={isLanguageAvailable(Language.ES)} />
                        <LanguageItem value={"fr"} label={t("HEADER.LANGUAGE.FRENCH")} show={isLanguageAvailable(Language.FR)} />
                        <LanguageItem value={"nl"} label={t("HEADER.LANGUAGE.DUTCH")} show={isLanguageAvailable(Language.NL)} />
                        <LanguageItem value={"it"} label={t("HEADER.LANGUAGE.ITALIAN")} show={isLanguageAvailable(Language.IT)} />
                    </LanguageSelector>

                    <NavigationLink label={t("HEADER.MENU")} icon={"icon-menu"} tabindex={0}>
                        <LinkItem href="/" label={t("HEADER.HOME")} icon={"icon-household"} show={true} />
                        <LinkItem
                            href="/services/contactdata"
                            label={t("HEADER.CONTACT_UPDATE")}
                            icon={"icon-digital-processes-V3"}
                            show={isMenuLinkAvailable(NavigationPage.CONTACT_DATA)}
                        />
                        <LinkItem
                            href="/contact_es/new_address_contact.xhtml"
                            label={t("HEADER.CONTACT_UPDATE")}
                            icon={"icon-digital-processes-V3"}
                            show={isMenuLinkAvailable(NavigationPage.CONTACT_DATA_OLD)}
                        />
                        <LinkItem href="/services/contactform" label={t("HEADER.CONTACT")} icon={"icon-exchange-V1"} show={isMenuLinkAvailable(NavigationPage.CONTACT_FORM)} />
                        <LinkItem href="/contact_es/" label={t("HEADER.CONTACT")} icon={"icon-exchange-V1"} show={isMenuLinkAvailable(NavigationPage.CONTACT_FORM_OLD)} />
                        <LinkItem
                            href="/services/rateagreement/ratenzahlung"
                            label={t("HEADER.RATEAGREEMENT")}
                            icon={"icon-rate-agreement-notes"}
                            show={isMenuLinkAvailable(NavigationPage.RATE_AGREEMENT)}
                        />
                        <LinkItem href="/services/content/download.html" label={t("HEADER.FORMS")} icon={"icon-download"} show={isMenuLinkAvailable(NavigationPage.DOWNLOAD)} />
                        <LinkItem href={getHelpLink()} label={t("HEADER.HELP")} icon={"icon-questions-V3"} show={isMenuLinkAvailable(NavigationPage.HELP)} />

                        <LinkItem
                            isMobileOnly={true}
                            show={true}
                            label={t("HEADER.LANGUAGE.TITLE")}
                            icon={"icon-global-service"}
                            subItems={[
                                { label: t("HEADER.LANGUAGE.GERMAN"), onClick: () => changeLanguageHeader("de"), show: isLanguageAvailable(Language.DE) },
                                { label: t("HEADER.LANGUAGE.ENGLISH"), onClick: () => changeLanguageHeader("en"), show: isLanguageAvailable(Language.EN) },
                                { label: t("HEADER.LANGUAGE.POLISH"), onClick: () => changeLanguageHeader("pl"), show: isLanguageAvailable(Language.PL) },
                                { label: t("HEADER.LANGUAGE.TUERKISCH"), onClick: () => changeLanguageHeader("tr"), show: isLanguageAvailable(Language.TR) },
                                { label: t("HEADER.LANGUAGE.SPANISH"), onClick: () => changeLanguageHeader("es"), show: isLanguageAvailable(Language.ES) },
                                { label: t("HEADER.LANGUAGE.FRENCH"), onClick: () => changeLanguageHeader("fr"), show: isLanguageAvailable(Language.FR) },
                                { label: t("HEADER.LANGUAGE.DUTCH"), onClick: () => changeLanguageHeader("nl"), show: isLanguageAvailable(Language.NL) },
                                { label: t("HEADER.LANGUAGE.ITALIAN"), onClick: () => changeLanguageHeader("it"), show: isLanguageAvailable(Language.IT) },
                            ]}
                        />
                    </NavigationLink>
                </nav>
            </div>
        </div>
    );
};

export default Header;
